/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Serialize Classroom directory, lifecycle, roster, and member-removal requests through the authenticated API client.
 * @state-owner request-local transport
 * @allowed-imports API client public type; same-module model types
 * @forbidden React; query cache; routes
 * @public-api same-module Classroom queries and mutations
 * @side-effects performs authenticated Classroom reads and idempotent create/lifecycle/member-removal commands
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key
 * @tests classroom-directory.test.ts and ../components/ClassroomMemberManagement.test.tsx
 * @security-boundary browser workspace to authenticated FastAPI Classroom endpoints
 * @data-classification tenant-scoped Classroom names, subjects, member summaries, and opaque idempotency keys
 * @trust-model caller values are untrusted transport input; FastAPI validates schema and re-authorizes actor/Organization scope
 */

import type { VispoApiClient } from '@vispo/api-client';
import type { ExistingClassMemberResponse } from '../model/types';

export interface ClassroomStudentSummaryApiResponse {
  classroom_id: string;
  student_id: string;
  from: string;
  to: string;
  records: Array<{
    submission_id: string;
    student_assignment_id: string;
    assignment_id: string;
    kind: 'original' | 'correction';
    source: 'student' | 'live_close';
    submitted_at: string;
    released_result: {
      id: string;
      revision_no: number;
      release_source: 'automatic' | 'teacher';
      total_score: number;
      max_score: number;
      overall_feedback: string | null;
      available_at: string;
      released_at: string;
    } | null;
    attention_signals: Array<
      'submission_not_released' | 'partial_released_result' | 'correction_submission'
    >;
  }>;
}

export interface ClassroomDirectoryApiResponse {
  id: string;
  organization_id: string;
  name: string;
  subject?: string | null;
  student_count: number;
  join_code: string;
  revision: number;
  status?: 'active' | 'archived';
  can_edit_basic_details?: boolean;
  can_manage_lifecycle?: boolean;
}

export interface ClassroomCreateCommand {
  idempotencyKey: string;
  name: string;
  organizationId: string;
  subject?: string;
}

export interface ClassroomUpdateCommand {
  expectedRevision: number;
  idempotencyKey: string;
  name: string;
  organizationId: string;
  subject: string | null;
}

export interface ClassroomTransitionCommand {
  expectedRevision: number;
  idempotencyKey: string;
  organizationId: string;
}

export interface ClassroomMemberRemovalCommand {
  idempotencyKey: string;
}

export interface ClassroomLeaveCommand {
  idempotencyKey: string;
}

export interface ClassroomMemberRemovalApiResponse {
  classroomMemberId: string;
  classroomId: string;
  userId: string;
  status: 'removed';
}

export function classroomDirectoryRequest(apiClient: VispoApiClient, organizationId: string) {
  const query = new URLSearchParams({ organizationId, status: 'active' });
  return apiClient.request<ClassroomDirectoryApiResponse[]>(`/api/v1/classrooms?${query}`);
}

export function archivedClassroomDirectoryRequest(
  apiClient: VispoApiClient,
  organizationId: string,
) {
  const query = new URLSearchParams({ organizationId, status: 'archived' });
  return apiClient.request<ClassroomDirectoryApiResponse[]>(`/api/v1/classrooms?${query}`);
}

export function classroomMembershipDirectoryRequest(
  apiClient: VispoApiClient,
  organizationId: string,
) {
  const query = new URLSearchParams({ organizationId, status: 'active' });
  return apiClient.request<ClassroomDirectoryApiResponse[]>(
    `/api/v1/classrooms/memberships?${query}`,
  );
}

export function classroomCreateRequest(apiClient: VispoApiClient, command: ClassroomCreateCommand) {
  return apiClient.request<ClassroomDirectoryApiResponse>('/api/v1/classrooms', {
    method: 'POST',
    body: {
      idempotencyKey: command.idempotencyKey,
      name: command.name,
      organizationId: command.organizationId,
      ...(command.subject ? { subject: command.subject } : {}),
    },
  });
}

export function classroomUpdateRequest(
  apiClient: VispoApiClient,
  classroomId: string,
  command: ClassroomUpdateCommand,
) {
  return apiClient.request<ClassroomDirectoryApiResponse>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}`,
    {
      method: 'PATCH',
      body: {
        expectedRevision: command.expectedRevision,
        idempotencyKey: command.idempotencyKey,
        name: command.name,
        organizationId: command.organizationId,
        subject: command.subject,
      },
    },
  );
}

export function classroomTransitionRequest(
  apiClient: VispoApiClient,
  classroomId: string,
  transition: 'archive' | 'restore',
  command: ClassroomTransitionCommand,
) {
  return apiClient.request<ClassroomDirectoryApiResponse>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/${transition}`,
    {
      method: 'POST',
      body: {
        expectedRevision: command.expectedRevision,
        idempotencyKey: command.idempotencyKey,
        organizationId: command.organizationId,
      },
    },
  );
}

export function classroomMembersRequest(apiClient: VispoApiClient, classroomId: string) {
  return apiClient.request<ExistingClassMemberResponse[]>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/members`,
  );
}

export function classroomStudentMembersRequest(apiClient: VispoApiClient, classroomId: string) {
  const query = new URLSearchParams({ role: 'student' });
  return apiClient.request<ExistingClassMemberResponse[]>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/members?${query}`,
  );
}

export function classroomStudentSummaryRequest(
  apiClient: VispoApiClient,
  classroomId: string,
  studentId: string,
  from: string,
  to: string,
) {
  const query = new URLSearchParams({ from, to });
  return apiClient.request<ClassroomStudentSummaryApiResponse>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/students/${encodeURIComponent(studentId)}/summary?${query}`,
  );
}

export function classroomMemberRemovalRequest(
  apiClient: VispoApiClient,
  classroomId: string,
  memberUserId: string,
  command: ClassroomMemberRemovalCommand,
) {
  return apiClient.request<ClassroomMemberRemovalApiResponse>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/members/${encodeURIComponent(memberUserId)}`,
    {
      method: 'DELETE',
      body: { idempotencyKey: command.idempotencyKey },
    },
  );
}

export function classroomLeaveRequest(
  apiClient: VispoApiClient,
  classroomId: string,
  command: ClassroomLeaveCommand,
) {
  return apiClient.request<void>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/me/membership`,
    {
      method: 'DELETE',
      body: { idempotencyKey: command.idempotencyKey },
    },
  );
}
